import moment from "moment";
import {history} from "umi";
import {cloneDeep, isEmpty} from "lodash-es";
import {getCycleTime, isFilter} from "../../pages/WorkBench/CreateReport/utils";
import {saveChartExploreFilters} from "@/pages/datalab/components/DashboardLayout/components/DragShell/_utils";
import {getLocaleState} from "@home/utils/state";
import {getLastDateRange} from "@/pages/datalab/utils/utils";
import {formatDateRange, getDateRange} from "@dbl/core/date";
import { ILayoutItemFilter } from '@dbl/core/dashboard';
import type { IFilterValueRequest } from '@dbl/services/dashboard';
import {setChartAnalysisParams} from "@home/components/CommonDashboardReport/hooks";
import {IUserGroup} from "@/pages/datalab/components/UserFilter/FilterPanel/type";
import {
  DEFAULT_NAME,
  DEFAULT_NAME_CN,
  DEFAULT_NAME_EN
} from "@/pages/datalab/components/UserFilter/FilterPanel/constant";
import {v4 as uuidv4} from "uuid";
import {storage} from "@/utils/utils";
import {CHART_EXPLORE_USER_FILTER} from '@dbl/core/global';

// 获取url参数键值对,针对特殊字符进行反转，用useLocation不兼容特殊字符，会转义
export const queryParams = (filterConfig: any)=> filterConfig?.match(/([^?=&]+)(=([^&]*))/g)
  ?.reduce((a, v) => {
    const key = v.slice(0, v.indexOf('='));
    const value = v.slice(v.indexOf('=') + 1);
    a[key] = value;
    return a;
  }, {});

export const renderFiterValue = (filters: ILayoutItemFilter[] = [], isConfirm = false, filterConfig?:string) => {
  const filterValue: any = {};
  filters?.map((data: any) => {
    if (!isEmpty(data)) {
      data.i = data?.i ?? data?.filterKey;
    }
    const {
      i,
      defaultType,
      value,
      column,
      dataset,
      filterType,
      defaultValue,
      operation,
      isFull,
      newIsFull,
      timeType,
      dateType,
      defaultOperation,
      scope,
      filterName = '',
      defaultDeleteValues,
      otherInfo,
      chartId,
      scope_value: scopeValue,
      selected_count: selectedCount,
      delete_values: deleteValues,
      scope_charts: scopeCharts,
      scope_type: scopeType,
      sql_expression: sqlExpression,
      table_id: tableId,
    } = data || {};
    if (filterType) {
      let newValue = isConfirm ? value : defaultValue;
      switch (filterType) {
        case 'country': newValue = newValue?.markets;break;
        case 'date': {
          if (typeof newValue === 'string') {
            // 兼容旧数据
            newValue = getCycleTime(defaultType, newValue);
          }
          break;
        }
        case 'string': {
          // 需要判断以下场景
          // 1. 默认进入 需要使用 isFull判断字符串是否全选 (isConfirm 为 false)
          // 2. 点击confirm 需要用 newIsFull 判断是否全选; 但早期旧数据中没有newIsFull字段, 就需要用isFull进行判断 (isConfirm 为 true)
          // 3. 点击reset 需要用isFull判断字符串是否全选 (isConfirm 为 false)
          let full;
          if (isConfirm) {
            full = newIsFull == undefined ? isFull : newIsFull;
          } else {
            full = isFull;
          }
          // eslint-disable-next-line no-nested-ternary
          newValue = full ? data.urlValue ? value : [] : newValue;
        }
      }
      if (filterConfig) {
        // 处理动机：在获取info接口完毕之后，会并行2个接口，其中一个获取当前报表下的批量数据，一个获取报表筛选器下拉数据，如果管理端配置一些额外信息，
        // 此时需要针对筛选器下拉列表进行取实际交集，此时的筛选器下拉接口还没返回，没法比较。如果要比较，需要等待筛选器接口返回，在比对
        // 致命会拉低整体页面渲染时长，先偷偷摸摸的传给后端配置所有信息，已确认。如果是无效的value，在接口源头处不会暴露出来，但是在筛选器列表中，进行比较，取实际交集，底层组件监听了filterValue, 待优化
        if (isFilter(i)) {
          // 过滤专属筛选器
          if((filterConfig?.hiddenList?.split(',')?.includes(i))){
            data.urlHidden = true;
          }
          data.filterConfig = filterConfig;
          const initialValue = setChartAnalysisParams(data, [], true);
          // 一定是配置了有效的key才进的来这段逻辑
          if (!isEmpty(initialValue)) {
            newValue = initialValue;
            data.defaultSelectedCount = initialValue?.length;
            data.urlValue = initialValue;
            if (filterType === 'date') {
              const timeFormat = moment(
                initialValue?.[0],
                'YYYY-MM',
                true,
              ).isValid()
                ? 'month'
                : 'date';
              data.timeType = timeFormat;
              data.defaultType = timeFormat;
            }
          }
        }
      }
      const newSelectedCount = selectedCount || 0;
      const defaultCount = data.defaultSelectedCount === undefined ? newSelectedCount : data.defaultSelectedCount;
      const newTimeType = timeType || dateType;
      filterValue[i] = {
        value: newValue,
        selected_count: isConfirm ? newSelectedCount : defaultCount,
        delete_values: (isConfirm ? deleteValues : defaultDeleteValues) || [],
        operation: (isConfirm ? operation : (defaultOperation || operation)) || (filterType === 'string' ? 'include' : 'between'), // defaultOperation || operation (filterType === 'string' ? 'include' : 'between') 是为了兼容旧数据
        column,
        table_id: dataset ?? tableId,
        column_type: filterType,
        dateType: (isConfirm ? (newTimeType === 'date' ? 'day' : newTimeType) : (data.defaultType === 'date' ? 'day' : data.defaultType)), // defaultType判断兼容旧数据
        scope_charts: cloneDeep(scope?.scope_charts ?? scopeCharts),
        scope_type: scope?.scope_type ?? scopeType,
        chartId: otherInfo?.chartId ?? chartId,
        filterName,
        sql_expression: otherInfo?.sqlExpression ?? sqlExpression,
        filterKey: data?.i,
        filterType,
        newIsFull,
        scope_value: scopeValue
      };
    }
  });
  return filterValue || {};
};

export const renderFiterState = (filters: ILayoutItemFilter[] = []) => {
  const filterValue: any = {};
  filters?.map((data: any) => {
    const { i, defaultType, value, column, dataset, filterType, defaultValue, isFull, operation, defaultOperation } = data || {};
    if (filterType) {
      filterValue[i] = {
        ...data,
        value: defaultValue || value,
        operation: defaultOperation || operation || (filterType === 'string' ? 'include' : 'between'),
        column,
        table_id: dataset,
        column_type: filterType,
        newIsFull: isFull,
      };
      // 兼容老数据（国家筛选器值为数组的情况）
      if (filterType === 'country' && Array.isArray(value)) {
        filterValue[i].value = {
          regions: [],
          markets: value,
          region_type: 'market',
        };
      }
      if (filterType === 'date') {
        filterValue[i].defaultType = defaultType;
        filterValue[i].defaultValue = defaultValue;
        filterValue[i].timeType = data?.timeType;
      }
    }
  });
  return filterValue || {};
};

// 只在报表页面初始化 或者 点击重置按钮时调用, 将报表信息info中的筛选器值转换成上传接口结构.
export const getAllFilterDefaultValues: (filters: ILayoutItemFilter[]) => Record<string, IFilterValueRequest> = (filters = []) => {
  const filterValue: any = {};
  filters?.map((data: any) => {
    
    const {
      i=data?.filterKey,
      defaultType,
      // value,
      column,
      dataset,
      filterType,
      defaultValue,
      defaultCompareTimes,
      defaultCustomValues,
      operation,
      defaultOperation,
      isFull,
      newIsFull,
      // timeType,
      // dateType,
      scope,
      filterName = '',
      otherInfo,
      chartId,
      scope_value: scopeValue,
      scope_charts: scopeCharts,
      scope_type: scopeType,
      sql_expression: sqlExpression,
      table_id: tableId,
      last_day,
      lastCount,
      quickValue,
    } = data || {};
    
    if (filterType) {
      let newValue = defaultValue;
      let newOperation = (defaultOperation || operation) || (filterType === 'string' ? 'include' : 'between'); // defaultOperation || operation (filterType === 'string' ? 'include' : 'between') 是为了兼容旧数据
      // let is_full = isFull ?? newIsFull;
      switch (filterType) {
        case 'country': newValue = newValue?.markets;break;
        case 'date': {
          if (typeof newValue === 'string') {
            // 兼容旧数据
            newValue = getCycleTime(defaultType, newValue);
          } else if (defaultOperation === 'recent') {
            // 默认进入报表或重置筛选器时,
            // 日期筛选器如果是动态计算的, 要上传给后端静态的数据
            newOperation = 'between';
            const format = defaultType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
            // lastCount如果是空字符串, 则从默认值中获取
            // lastCount 是数字0, 从默认值中获取到的也是数字0
            const newLastCount = lastCount || Number(defaultValue?.[0]?.match(/^(-?\d+)-/)[1]);
            newValue = getLastDateRange(newLastCount, defaultType, last_day, format);
          }
          break;
        }
        case 'string': {
          newValue = (isFull ?? newIsFull) ? [] : newValue;
        }
      }
      
      // const newTimeType = timeType || dateType; // TODO 是不是有的旧数据中 用两个名称代表 日期粒度???
      
      filterValue[i] = {
        last_day,
        value: newValue,
        compare_time: defaultCompareTimes,
        operation: newOperation,
        is_full: isFull ?? newIsFull,
        customValues: defaultCustomValues ?? [],
        scope_value: scopeValue, // 虚拟版本有对筛选器进行配置选项的, 存储在这里, 上传给后端
        dateType: (data.defaultType === 'date' ? 'day' : data.defaultType),
  
        // 报表筛选器会影响哪些图表
        scope_charts: cloneDeep(scope?.scope_charts ?? scopeCharts),
        scope_type: scope?.scope_type ?? scopeType,
        
        column,
        table_id: dataset ?? tableId,
        column_type: filterType,
        
        // 自定义字段需要 chartId, sql_expression
        chartId: otherInfo?.chartId ?? chartId,
        sql_expression: otherInfo?.sqlExpression ?? sqlExpression,
        
        // newIsFull: isFull ?? newIsFull,
        
        filterKey: data?.i,
        filterType,
        filterName,
        quick_value: quickValue,
      };
    } else {
      // 空的筛选器, 赋空值是为了解决 只有空筛选器时, 无法渲染顶部筛选器
      filterValue[i] = {};
    }
  });
  return filterValue || {};
};

// 处理上传接口中筛选器数据的特殊结构
export const handleFilterValuesStruct: (filterValues: IFilterValueRequest[]) => IFilterValueRequest[] = (filterValues) => {
  try {
    const newValues: IFilterValueRequest[] = [];
    filterValues?.forEach(item => {
      if (!isEmpty(item)) { // 由于用户可以新增空的筛选器, 所以在上传的数据里去掉
        if (item.filterType === 'string') {
          // 字符串筛选器
          const cloneValue = cloneDeep(item); // value是引用类型, 改变value会影响StringFilter内部状态, 所以这里进行了clone
          cloneValue.value = cloneValue.is_full ? [] : cloneValue.value; // TODO 注意虚拟版本情况
          newValues.push(cloneValue);
          cloneValue.filter_flag = 1; // 为了区分新旧数据结构 上传一个特定值
        } else if (item.filterType === 'date') {
          if (item.compare_time && item.compare_time.length > 0) {
            item.compare_time = item.compare_time.map((compare) => {
              if (compare?.time_range && isEmpty(compare.last_time)) return compare;
              const {last_time, granularity} = compare || {};
              const range = getDateRange(item?.value || [], {amount: last_time, granularity, timeGranularity: item.dateType});
              return {
                ...compare,
                time_range: formatDateRange(range, item.dateType),
              };
            });
          }
          newValues.push(item);
        } else {
          newValues.push(item);
        }
      }
    });
    return newValues;
  } catch (e) {
    console.log("handleFilterValuesStruck error", e);
    return filterValues;
  }
};

// 获取虚拟版本管理端配置的字符串筛选器的值
export const getOptionsOfVirtual = (
  item: { i: any; filterConfig: { [x: string]: string; }; filterType: string; },
  optionUrlStr: string
) => {

  if (optionUrlStr) {
    
    const newSplitStr = '|$|';
    const oldSplitStr = ',';
    const splitStr = optionUrlStr.includes(newSplitStr) ? newSplitStr : oldSplitStr;
    // 数据源
    return optionUrlStr
      ?.split(splitStr)
      .filter((task: string) => 
        task // 过滤空值
      );
  }
};

/**
 * 同一张报表中有多个 图表卡片
 * 当其中一张图表卡片的数据量很大, loading加载时间很长时, 用户连续点击其他卡片的探索按钮时, 会被主线程阻塞click事件的执行, 也就是用户点击后没有立即跳转, 而是等待loading和渲染结束, 导致后面会执行多次click;
 * 用户点击几次click, 就会执行几次click事件, 当执行到第二次click事件时, 内部逻辑判断已发生错误, 导致跳转错误.
 *  测试地址: http://databrain-test.intlgame.com/v2/datalab/test/WorkBench/MyDashboard/ViewPage?dashboardId=4f8fb8be0e9dde592cc0c30f9623a943&dashboardName=%E5%A4%A7%E6%95%B0%E6%8D%AE%E9%87%8F%E5%8A%A0%E8%BD%BD%E9%98%BB%E5%A1%9E%E6%B5%8B%E8%AF%95&dashboardStatus=0&isTemplateReport=false
 *
 * 针对上面的主线程被堵塞的情况, 考虑以下情况:
 *  1. 用户连续点击的是同一个卡片的同一个按钮, 比如连续点击探索按钮 (主要场景)
 *  2. 用户连续点击的是同一个卡片的不同按钮, 比如点击了探索按钮, 又点击了下载按钮.
 *  3. 用户连续点击的是不同卡片的按钮, 比如点击了A卡片的探索按钮, 又点击B卡片的探索按钮, 或者下载按钮
 *
 *  解决方案思路:
 *  思路1:
 *     等到所有卡片的loading结束后, 才允许用户点击按钮, 保证点击事件不会出错
 *     缺点: 原本单独卡片的loading状态管理需要提升到父级, 影响所有卡片; 且用户体验上不太好.
 *
 *  思路2:
 *     对所有卡片click事件做统一的防抖功能, 尽量实现 只执行用户的最后一次点击.
 *     缺点: 防抖的时间间隔不好确定, 间隔短了可能也会执行多次click事件导致错误, 间隔长了平时用户在使用时也可能会感觉卡顿的效果
 *
 *  思路3: 只执行第一次用户点击
 *     设置标志位, 执行用户第一次点击时,设置为true, 后面的click事件都不执行
 *     缺点: 用户可能会觉得没有执行最后一次click事件有些奇怪.
 *
 */

let isClickedCardBtn = false;
export const changeIsClickedCardBtn  = (value: boolean) => (isClickedCardBtn = value);
// 点击探索按钮事件
export const handleExportClick = ({filters, system, gamecode, dataMsg, userFilterInfo}) => {
  if (isClickedCardBtn) return; // 用户点击过就不在继续执行
  isClickedCardBtn = true; // 用户点击过
  saveChartExploreFilters(filters || []);
  // 保存 user filter 数据到SessionStorage
  userFilterInfo?.controls?.length > 0 && storage.setSessionStorageItem(CHART_EXPLORE_USER_FILTER, userFilterInfo);
  // 报表预览页面的跳转, 和 其他系统的承接页面的跳转不同
  const isDatalab = history.location.pathname.includes('WorkBench/MyDashboard/ViewPage');
  if (!isDatalab) {
    getLocaleState('datalabMessageBus')?.notifyExploreButtonClick({chartId: dataMsg.task_id, chartName: dataMsg?.name, nameCn: dataMsg?.name_cn});
  }
  history.push({
    // 需要区分承接页面和Datalab的不同路径
    pathname: isDatalab ? `/${system}/${gamecode}/Analysis/MultiAnalysis` : `/datalab/iframe/explore`,
    query: {
      from: isDatalab ? 'datalabExplore' : 'dashboardExplore', // 需要区分承接页面和Datalab的不同配置
      taskId: dataMsg.task_id,
      name: dataMsg?.name,
      nameCn: dataMsg?.name_cn,
    }
  });
  setTimeout(() => {
    isClickedCardBtn = false;
  }, 100);
};

// 创建用户筛选中空的对照组
export const createEmptyUserControl = (config?: { defaultName?: string; defaultNameEn?: string; defaultNameCn?: string;}): IUserGroup => {
  const {defaultNameEn, defaultNameCn, defaultName } = config ?? {};
  const mergedDefaultName = defaultName || defaultNameEn || defaultNameCn || DEFAULT_NAME;
  const mergedDefaultNameEn = defaultNameEn || DEFAULT_NAME_EN || mergedDefaultName;
  const mergedDefaultNameCn = defaultNameCn || DEFAULT_NAME_CN || mergedDefaultName;
  
  return {
    id: `group_${uuidv4()}`,
    name: mergedDefaultName,
    name_en: mergedDefaultNameEn,
    name_cn: mergedDefaultNameCn,
    isNameCustomized: false,
    filters: [
      {
        id: `filter_${uuidv4()}`,
        field: undefined,
        fieldType: undefined,
        operator: undefined,
        value: undefined,
        hasError: true,
      }
    ],
    logic: 'AND',
    summaryEn: mergedDefaultNameEn,
    summaryCn: mergedDefaultNameCn,
    is_all_users: true,
  };
};